import { useContext, useState, useEffect } from "react";
import { AppContext } from '../../application/provider';
import NavigationLinks from '../../components/Navigation/NavigationLinks';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Grid, Menu, X } from 'lucide-react';
import { BellSolid, GridSolid, SettingsSolid, SentinelSolid, AgencySolid } from '../Icons/FlowbiteIcons';
import LogoPerfil from '../../img/logo_perfil.webp';
import SidebarFondo from '../../img/sidebar_fondo.webp';
import NotificacionesService from '../../axios_services/notificaciones.service';

function SideBar({ onLogout }) {
  const [state] = useContext(AppContext);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (Number(state.user?.perfil_id) !== 8) return;

    const fetchConteo = () => {
      NotificacionesService.conteoNoLeidas()
        .then(data => setAlertCount(data.total || 0))
        .catch(() => {});
    };

    fetchConteo();
    const intervalo = setInterval(fetchConteo, 30000);

    const handler = (e) => setAlertCount(e.detail.count);
    window.addEventListener('mec-notif-update', handler);

    return () => {
      clearInterval(intervalo);
      window.removeEventListener('mec-notif-update', handler);
    };
  }, [state.user]);

  const handleLogoutClick = async () => {
    setIsLoggingOut(true);
    try {
      await onLogout();
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const toggleOpen = () => setIsOpen(!isOpen);

  return (
    <>
      {!isOpen && (
        <button
          onClick={toggleOpen}
          type="button"
          className="fixed top-4 right-4 z-50 inline-flex items-center p-2 text-[#c4c9d0] rounded-base sm:hidden bg-neutral-primary-soft/80 border border-[#c4c9d0] hover:bg-[#59667a] hover:text-white hover:border-white focus:outline-none focus:ring-4 focus:ring-neutral-tertiary"
        >
          <span className="sr-only">Open sidebar</span>
          <Menu className="w-6 h-6" />
        </button>
      )}

      <aside
        className={`fixed top-0 left-0 z-40 w-72 h-full transition-transform ${isOpen ? 'translate-x-0' : '-translate-x-full'
          } sm:translate-x-0 border-e border-default overflow-hidden`}
      >
        <div
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${SidebarFondo})` }}
        >
        </div>

        <div className="h-full px-3 pt-4 pb-20 flex flex-col relative z-10 overflow-y-auto">
          <div className="flex justify-end sm:hidden">
            <button
              onClick={toggleOpen}
              className="p-1 text-heading rounded-base hover:bg-neutral-secondary-medium"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex flex-col items-center mb-6 px-2 mt-4">
            <div className="w-24 h-24 rounded-full bg-neutral-secondary-medium mb-3 overflow-hidden flex items-center justify-center">
              <img
                src={LogoPerfil}
                alt="Profile"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-10 h-10 text-[#c4c9d0]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                }}
              />
            </div>
            <h2 className="text-white font-semibold text-lg text-center break-words w-full px-2">{state.user?.nombres || 'Usuario'}</h2>
            <p className="text-white text-xs truncate w-full text-center opacity-80">{state.user?.usuario}</p>

            <button
              onClick={handleLogoutClick}
              disabled={isLoggingOut}
              className="mt-5 flex items-center justify-center w-full p-2 text-[#c4c9d0] border border-[#c4c9d0] rounded-base hover:bg-[#59667a] hover:text-white hover:border-white transition-all group text-sm"
            >
              <LogOut className="w-4 h-4 me-2 transition-colors group-hover:text-white" />
              <span>{isLoggingOut ? "Saliendo..." : "Cerrar Sesión"}</span>
            </button>

            <div className="grid grid-cols-3 gap-4 mt-6 mb-2 -mx-2">
              <button
                onClick={() => navigate('/')}
                className={`${location.pathname === '/' ? 'text-white' : 'text-[#c4c9d0]'} hover:text-white transition-colors`}
                title="Menú Principal"
              >
                <GridSolid size={24} />
              </button>
              {/* {Number(state.user?.perfil_id) !== 28 && (
                <>
                  <button
                    onClick={() => window.open("https://www.sentinelperu.com/cliente/sentinel.aspx", "_blank")}
                    className="text-[#c4c9d0] hover:text-white transition-colors"
                    title="Sentinel"
                  >
                    <SentinelSolid size={24} />
                  </button>
                  <button
                    onClick={() => window.open('/#/agencias', '_blank')}
                    className="text-[#c4c9d0] hover:text-white transition-colors"
                    title="Agencias"
                  >
                    <AgencySolid size={24} />
                  </button>
                </>
              )} */}
              <button
                onClick={() => navigate('/notificaciones')}
                className={`relative ${location.pathname === '/notificaciones' ? 'text-white' : 'text-[#c4c9d0]'} hover:text-white transition-colors`}
                title="Notificaciones"
              >
                <BellSolid size={24} />
                {alertCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full flex items-center justify-center font-bold" style={{ fontSize: '9px', width: '16px', height: '16px', lineHeight: 1 }}>
                    {alertCount > 9 ? '9+' : alertCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => navigate('/perfil')}
                className={`${location.pathname === '/perfil' ? 'text-white' : 'text-[#c4c9d0]'} hover:text-white transition-colors`}
                title="Configuración"
              >
                <SettingsSolid size={24} />
              </button>
            </div>
          </div>

          <div className="border-t border-[#c4c9d0] my-4 opacity-50"></div>

          <div className="flex-1">
            <NavigationLinks
              perfilId={state.user?.perfil_id}
              onLinkClick={() => setIsOpen(false)}
            />
          </div>
        </div>
      </aside>
    </>
  );
}

export default SideBar;